import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import SingleDayPicker from '../../../base/date/single-date-picker';
import { dateService } from '../../../../services/date';
import './date-picker.scss';
import { getClass } from '../../../../utils/component-helpers';

const DateRangePicker = props => {
  const {
    datetimeStart,
    datetimeEnd,
    onChange,
    startDateField,
    endDateField,
    endDateOptional,
    errors,
    disableStartDate,
    disableEndDate,
    rangeLimit,
  } = props;
  const [dateRange, setDateRange] = useState({
    startDate: datetimeStart ? moment(datetimeStart).utc() : null,
    endDate: datetimeEnd ? moment(datetimeEnd).utc() : null,
  });

  useEffect(() => {
    const newDate = {
      startDate: moment(datetimeStart).isValid() ? moment(datetimeStart).utc() : null,
      endDate: moment(datetimeEnd).isValid() ? moment(datetimeEnd).utc() : null,
    };

    setDateRange(newDate);
  }, [datetimeStart, datetimeEnd]);

  const updateDate = (key, date) => {
    const isValidDate = moment(date).isValid();
    const utcDate = isValidDate ? date.utc() : null;
    const startOrEndOfDay = utcDate
      ? key === startDateField
        ? utcDate.startOf('day')
        : utcDate.endOf('day')
      : null;

    const utcDateTime = isValidDate ? dateService.toIso8601UtcDatetime(startOrEndOfDay) : null;
    const newValue = { target: { value: isValidDate ? utcDateTime : null, name: key } };

    onChange(newValue);
  };

  return (
    <div className="date-range-picker-v2">
      <div className={getClass('single-picker', { invalid: errors[startDateField] })}>
        <SingleDayPicker
          id="start-date"
          placeholder="Start Date"
          date={dateRange.startDate}
          isOutsideRange={day =>
            day.isBefore(dateService.getStartOfDay(moment())) ||
            (rangeLimit[0] && day.isBefore(dateService.getEndOfDay(moment(rangeLimit[0])))) ||
            (rangeLimit[1] && day.isAfter(dateService.getEndOfDay(moment(rangeLimit[1]))))
          }
          setDate={date => updateDate(startDateField, date)}
          disabled={disableStartDate}
        />
        <div className="error-messages">
          {errors[startDateField] &&
            errors[startDateField].map(message => <div key={message}>{message}</div>)}
        </div>
      </div>
      <span className="to">to</span>
      <div className="single-picker">
        <SingleDayPicker
          id="end-date"
          placeholder="End Date"
          date={dateRange.endDate}
          isOutsideRange={day =>
            day.isBefore(dateRange.startDate) ||
            day.isBefore(dateService.getStartOfDay(moment())) ||
            (rangeLimit[0] && day.isBefore(dateService.getEndOfDay(moment(rangeLimit[0])))) ||
            (rangeLimit[1] && day.isAfter(dateService.getEndOfDay(moment(rangeLimit[1]))))
          }
          setDate={date => updateDate(endDateField, date)}
          disabled={disableEndDate}
        />
        <div className="sub-label">{endDateOptional && 'Optional'}</div>
        <div className="error-messages">
          {errors[endDateField] &&
            errors[endDateField].map(message => <div key={message}>{message}</div>)}
        </div>
      </div>
    </div>
  );
};

DateRangePicker.propTypes = {
  onChange: PropTypes.func,
  errors: PropTypes.shape({}),
  disableStartDate: PropTypes.bool,
  disableEndDate: PropTypes.bool,
  rangeLimit: PropTypes.arrayOf(PropTypes.string),
};

DateRangePicker.defaultProps = {
  onChange: () => {},
  startDateField: 'startDate',
  endDateField: 'endDate',
  errors: {},
  disableStartDate: false,
  disableEndDate: false,
  rangeLimit: [null, null],
};

export default DateRangePicker;
